import type { FilterConfig, FilterState } from '../types';
import { applyFilters } from './applyFilters';

/**
 * Compute per-value counts for an attribute, with all *other* active filters
 * applied. Used to render dynamic counts in the value picker so each option
 * shows how many rows would match if it were toggled on.
 *
 * If the attribute's value is an array (e.g. tags), every element counts.
 */
export function computeOptionCounts<T>(
  rows: readonly T[],
  state: FilterState,
  attributeId: string,
  getValue: (row: T, attributeId: string) => string | string[] | null | undefined,
  filterConfigs?: readonly FilterConfig[],
): Map<string, number> {
  if (filterConfigs?.length) {
    const cfg = filterConfigs.find((c) => c.id === attributeId);
    if (!cfg || cfg.kind !== 'multi-select') {
      return new Map();
    }
  }
  const otherFilters = state.filters.filter((f) => f.attributeId !== attributeId);
  const matching = applyFilters(rows, { filters: otherFilters }, getValue, filterConfigs);
  const counts = new Map<string, number>();
  for (const row of matching) {
    const raw = getValue(row, attributeId);
    const values = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
    for (const v of values) {
      if (typeof v === 'string' && v.length > 0) {
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
    }
  }
  return counts;
}
